<template>
  <div v-if="mStep.params.status?.state === ApprovalApprovableState.Cancelled" class="tw-ml-3">
    <WdsStack justify-center gap-sm>
      <WdsText>
        <span class="tw-font-semibold">
          {{ mStep.name }}
        </span>
      </WdsText>
      <WdsText secondary>
        {{ mStep.params.date }}
      </WdsText>
    </WdsStack>
  </div>
  <BaseListRow
    v-else
    surface="grey"
    padding="none"
    :transparent="isUpcoming"
    class="tw-ml-3 tw-items-center"
    :class="[
      mStep.isCurrent ? 'tw-px-3 tw-pr-0 tw-py-0' : 'tw-p-3 tw-py-1',
      mStep.useTagForName && mStep.params.upComing ? 'tw-py-3' : '',
    ]"
  >
    <div class="tw-w-full tw-flex tw-items-center tw-justify-between">
      <div class="tw-gap-x-2">
        <div class="tw-flex tw-gap-x-2 tw-items-center">
          <WdsText v-if="mStep.stepNumber">{{ mStep.stepNumber }}.</WdsText>
          <WdsTooltip v-if="mStep.useTagForName" class="tw-pt-1" global>
            <WdsTag sm gray class="!tw-rounded-full">
              <div class="tw-max-w-36 tw-truncate">
                {{ mStep.name }}
              </div>
            </WdsTag>
            <template v-if="mStep.params.userGroup?.users?.data?.length" #tooltip-content>
              <WdsStack class="tw-mb-1 tw-font-bold tw-text-left" gap-sm>
                {{ mStep.name }}:
              </WdsStack>
              <div
                v-for="user in mStep.params.userGroup.users.data"
                :key="`tag_tooltip_${user.id}`"
                style="text-align: left"
                v-text="user.name"
              />
            </template>
          </WdsTooltip>
          <WdsText v-else>
            <span :class="{ 'tw-text-gray-700': mStep.params.upComing }" v-text="mStep.name" />
          </WdsText>
          <WdsTag
            v-if="mStep.params.status"
            sm
            class="!tw-rounded"
            :success="mStep.params.status.state === ApprovalApprovableState.Approved"
            :error="
              mStep.params.status.state === ApprovalApprovableState.Rejected ||
              mStep.params.status.state === ApprovalApprovableState.Cancelled
            "
            :warning="mStep.params.status.state === ApprovalApprovableState.Requested"
          >
            {{ mStep.params.status.label }}
          </WdsTag>
          <WdsText v-if="mStep.params.date && (isApproved || isRejected)" secondary>
            {{ mStep.params.date }}
          </WdsText>
        </div>
        <WdsText v-if="mStep.isCurrent" secondary class="tw-mt-2">
          {{ $t('Delivered :date', { date: mStep.params.date }) }}
        </WdsText>
      </div>

      <div
        v-if="!mStep.isCurrent"
        class="tw-flex tw-space-x-2 tw-items-center"
        :class="{ 'tw-min-h-[40px]': mStep.params.userGroup }"
      >
        <WdsAvatarLabel
          v-if="(isApproved || isRejected) && mStep.params.user && !isCancelled"
          :text="mStep.params.user.name"
          :path="getAvatarPath(mStep.params.user.avatar, mStep.params.user.id)"
          :initials="textHelpers.nameStringToInitials(mStep.params.user.name, 2)"
          truncated
          sm
        />
        <WdsTooltip v-else-if="mStep.params.userGroup && !isCancelled" global>
          <WdsAvatarGroup
            sm
            :avatars="
              mStep.params.userGroup.users.data.map((user: any) => ({
                path: getAvatarPath(user.avatar, user.id),
                initials: textHelpers.nameStringToInitials(user.name, 2),
              }))
            "
            :max-avatars="4"
            :total="mStep.params.userGroup.users.paginatorInfo.total"
          />
          <template #tooltip-content>
            <WdsStack class="tw-mb-1 tw-font-bold tw-text-left" gap-sm>{{ mStep.name }}:</WdsStack>
            <div
              v-for="user in mStep.params.userGroup.users.data"
              :key="`user_tooltip_${user.id}`"
              style="text-align: left"
              v-text="user.name"
            />
          </template>
        </WdsTooltip>
        <WdsAvatarLabel
          v-else-if="mStep.params.user && !isCancelled"
          :text="mStep.params.user.name"
          :path="getAvatarPath(mStep.params.user.avatar, mStep.params.user.id)"
          :initials="textHelpers.nameStringToInitials(mStep.params.user.name, 2)"
          truncated
          sm
        />
        <WdsText v-else-if="!isCancelled" secondary>-</WdsText>
      </div>

      <div v-else class="tw-flex tw-items-end tw-min-h-[75px]">
        <WdsTooltip global :class="['tw-pb-2.5', { 'tw-pr-2.5': !viewerCanAction }]">
          <WdsAvatarGroup
            v-if="mStep.params.userGroup"
            sm
            :avatars="
              mStep.params.userGroup.users.data.map((user: any) => ({
                path: getAvatarPath(user.avatar, user.id),
                initials: textHelpers.nameStringToInitials(user.name, 2),
              }))
            "
            :max-avatars="4"
            :total="mStep.params.userGroup.users.paginatorInfo.total"
          />
          <template #tooltip-content>
            <WdsStack class="tw-mb-1 tw-font-bold tw-text-left" gap-sm>{{ mStep.name }}:</WdsStack>
            <div
              v-for="user in mStep.params.userGroup.users.data"
              :key="`user_tooltip_${user.id}`"
              style="text-align: left"
              v-text="user.name"
            />
          </template>
        </WdsTooltip>
        <template v-if="viewerCanAction">
          <div
            class="tw-ml-4 tw-border-r-[1px] tw-h-[75px] tw-border-l-0 tw-border-solid tw-border-gray-200"
          ></div>
          <WdsButton
            primary
            data-test="review-approval-button"
            class="!tw-ml-4 !tw-mr-4 tw-self-center"
            @click.prevent="emit('show-approval-request-modal')"
          >
            {{ $t('Review') }}
          </WdsButton>
        </template>
      </div>
    </div>
    <div v-if="mStep.params.message" class="tw-rounded tw-mt-2 tw-px-4 tw-py-2 tw-bg-[#F6F6F6]">
      <p class="tw-m-0 tw-text-gray-800">
        {{ mStep.params.message }}
      </p>
    </div>
  </BaseListRow>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue'
import {
  WdsAvatarGroup,
  WdsAvatarLabel,
  WdsButton,
  WdsStack,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { ApprovalApprovableState } from '@/gql/graphql'
import BaseListRow from '@/components/BaseListRow.vue'
import { textHelpers } from '@/utils/tools'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import type { HireCreatedApprovableProgressStep } from '@/components/Workflows/Approvals/approvals-types'
import { getAvatarPath } from '@/utils/avatar'

const props = defineProps({
  mStep: {
    type: Object as PropType<HireCreatedApprovableProgressStep>,
    required: true,
  },
  viewerCanAction: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['show-approval-request-modal'])

const isCancelled = computed(
  () => props.mStep.params.status?.state === ApprovalApprovableState.Cancelled,
)

const isApproved = computed(
  () => props.mStep.params.status?.state === ApprovalApprovableState.Approved,
)

const isRejected = computed(
  () => props.mStep.params.status?.state === ApprovalApprovableState.Rejected,
)

const isUpcoming = computed(
  () =>
    props.mStep.params.status?.state !== ApprovalApprovableState.Approved &&
    props.mStep.params.status?.state !== ApprovalApprovableState.Rejected &&
    props.mStep.params.status?.state !== ApprovalApprovableState.Requested,
)
</script>
